import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BookableColorKey, getDefaultEventColorKey } from '../utils/calendarColors'
import { formatTimeStamp } from '../utils/dates'
import { BaseRecord } from './BaseRecord'
import { Bookable, ScheduleState } from './Bookable'
import { Mappable } from './Mappable'
import { RecordDataMap } from './RecordDataMap'
import { FKType } from './SQLBuilder'
import { User } from './User'

type PSEventConstructorArgs = ConstructorArgs<() => PSEvent>

export class PSEvent extends BaseRecord implements Bookable, Mappable {
    readonly recordType = 'psevent'

    readonly iBookable = true
    readonly iMappable = true

    static readonly PS_REGEX =
        /.*ID: ([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/i

    date?: moment.Moment
    endDate?: moment.Moment
    title?: string
    notes?: string
    url?: string
    location?: string
    externalId?: string
    device?: string

    modifiedDate?: moment.Moment
    creationDate?: moment.Moment
    cancelledDate?: moment.Moment
    importedDate?: moment.Moment

    constructor(data?: Partial<PSEventConstructorArgs>, params?: { id?: string }) {
        super(data)
        if (data instanceof RecordDataMap) {
            this.date = data.valueForTimeStampKey('date')
            this.title = data.valueForKey('title')
            this.url = data.valueForKey('url')
            this.notes = data.valueForKey('notes')
            this.location = data.valueForKey('location')
            this.endDate = data.valueForTimeStampKey('end_date')
            this.externalId = data.valueForKey('external_id')
            this.creationDate = data.valueForTimeStampKey('creation_date')
            this.modifiedDate = data.valueForTimeStampKey('modified_date')
        }
        if (params?.id) this.id = params.id
        if (!data) {
            return
        }

        this.title = data.title ?? undefined
        this.url = data.url ?? undefined
        this.notes = data.notes ?? undefined
        this.location = data.location ?? undefined
        this.date = this.propertyFromTimestampArg(data.date)
        this.endDate = this.propertyFromTimestampArg(data.end_date)
        this.device = data.device ?? undefined
        this.creationDate = this.propertyFromTimestampArg(data.creation_date)
        this.modifiedDate = this.propertyFromTimestampArg(data.modified_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.importedDate = this.propertyFromTimestampArg(data.imported_date)
    }

    clone(overrides?: Partial<PSEventConstructorArgs>): PSEvent {
        return new PSEvent({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): PSEvent {
        const event = new PSEvent(undefined, { id: id })
        return event
    }

    get description(): string {
        return `${this.date?.format('MMM D, YYYY')} - ${this.endDate?.format('MMM D, YYYY')}`
    }

    get coordinate(): [number, number] | undefined {
        return undefined
    }

    get shortTitle(): string | undefined {
        return this.title
    }

    get duration(): number | undefined {
        if (this.date && this.endDate) return this.endDate.diff(this.date, 'minutes')
        return undefined
    }

    get dateString(): string | undefined {
        const startString = this.date?.format('YYYYMMDDhhmmA')
        const untilString = this.endDate?.format('YYYYMMDDhhmmA')
        return startString && untilString ? `${startString}-${untilString}` : undefined
    }

    get isPS(): boolean {
        return PSEvent.isPS(this.notes)
    }

    static isPS(notes: string | undefined): boolean {
        if (notes) {
            const idString = notes.replace(/[\t\n\r]/g, ' ')
            const matcher = idString.match(PSEvent.PS_REGEX)
            if (matcher) console.log(`${idString}\n true`)
            return !!matcher
        }
        return false
    }

    get confirmedDate(): moment.Moment | undefined {
        return undefined
    }

    get person(): User | undefined {
        return undefined
    }

    get memo(): string | undefined {
        return this.notes
    }

    toString(): string {
        return this.description
    }

    // Bookable interface methods
    get color(): BookableColorKey {
        return getDefaultEventColorKey()
    }

    get employee(): User | undefined {
        return undefined
    }

    get allowJoin(): boolean {
        return false
    }

    get allowCheckin(): boolean {
        return false
    }

    get allowNotify(): boolean {
        return false
    }

    get hasSchedule(): boolean {
        return false
    }

    get isRecurring(): boolean {
        return false
    }

    get ownerIsMe(): boolean {
        return this.owner != undefined && this.owner.id === PSApplication.me().id
    }

    get scheduleStates(): ScheduleState[] {
        return [ScheduleState.Complete]
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.date) this.date = moment(data.date)
        if (data.endDate) this.endDate = moment(data.endDate)
        if (data.modifiedDate) this.modifiedDate = moment(data.modifiedDate)
        if (data.creationDate) this.creationDate = moment(data.creationDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.importedDate) this.importedDate = moment(data.importedDate)
    }

    static fk: FKType = {
        users: ['owner'],
    }

    static dataType(): DataType {
        return 'events'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['owner'] = PSApplication.me().id
        if (this.url) dict['url'] = this.url
        if (this.title) dict['title'] = this.title
        if (this.notes) dict['notes'] = this.notes
        if (this.location) dict['location'] = this.location
        dict['date'] = formatTimeStamp(this.date)
        dict['end_date'] = formatTimeStamp(this.endDate)
        if (this.creationDate) dict['creation_date'] = formatTimeStamp(this.creationDate)
        if (this.modifiedDate) dict['modified_date'] = formatTimeStamp(this.modifiedDate)
        if (this.cancelledDate) dict['cancelled_date'] = formatTimeStamp(this.cancelledDate)
        if (this.importedDate) dict['imported_date'] = formatTimeStamp(this.importedDate)
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
}
